import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';

export interface PauseDuration {
  pausedAt: any; // Timestamp
  resumedAt?: any; // Timestamp
  durationSeconds?: number; // Calculated when resumed
}

export interface ChecklistRun {
  id?: string;
  checklistId: string;
  checklistName?: string;
  
  // Context
  locationId?: string;
  locationName?: string;
  assetId?: string;
  assetName?: string;
  variableValues?: Record<string, string>;
  
  // Progress State
  status: 'in_progress' | 'paused' | 'completed';
  items?: Record<string, boolean>; // item text -> completed
  photos?: Record<string, string>; // item text -> photo URL (or temp storage path)
  itemTaskReferences?: Record<string, string[]>; // item text -> task IDs
  
  // Time Tracking
  startedAt: any; // Timestamp
  lastResumedAt?: any; // Timestamp - last time it was resumed (null if never paused)
  pausedAt?: any; // Timestamp - last time it was paused (null if never paused)
  completedAt?: any; // Timestamp - when checklist was completed
  activeTimeSeconds: number; // Cumulative active time in seconds
  pauseDurations?: PauseDuration[]; // Track each pause period
  
  // Metadata
  startedBy: string; // User ID who started
  startedByName?: string;
  completedBy?: string;
  completedByName?: string;
  organizationId?: string;
  owner_role?: string;
  source?: 'web' | 'mobile';
  
  // Completion reference (when completed)
  completionId?: string; // Reference to checklist_completions document
  
  createdAt?: any;
  updatedAt?: any;
}

export const checklistRunService = {
  // Create a new checklist run
  createRun: async (
    runData: Omit<ChecklistRun, 'id' | 'startedAt' | 'createdAt' | 'updatedAt' | 'activeTimeSeconds' | 'status'>
  ): Promise<{ success: boolean; id?: string; error?: string }> => {
    try {
      const runsRef = collection(db, 'checklist_runs');
      const now = serverTimestamp();
      const payload: Record<string, any> = {
        ...runData,
        status: 'in_progress',
        startedAt: now,
        lastResumedAt: now, // Initially same as startedAt
        activeTimeSeconds: 0,
        pauseDurations: [],
        createdAt: now,
        updatedAt: now,
      };

      // Remove undefined values
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const docRef = await addDoc(runsRef, payload);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error creating checklist run:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a checklist run by ID
  getRun: async (runId: string): Promise<{ success: boolean; data?: ChecklistRun; error?: string }> => {
    try {
      const runRef = doc(db, 'checklist_runs', runId);
      const docSnap = await getDoc(runRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as ChecklistRun };
      } else {
        return { success: false, error: 'Checklist run not found' };
      }
    } catch (error: any) {
      console.error('Error getting checklist run:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all active/paused runs for a user
  getActiveRunsForUser: async (
    userId: string
  ): Promise<{ success: boolean; data?: ChecklistRun[]; error?: string }> => {
    try {
      const runsRef = collection(db, 'checklist_runs');
      const q = query(
        runsRef,
        where('startedBy', '==', userId),
        where('status', 'in', ['in_progress', 'paused'])
      );
      const querySnapshot = await getDocs(q);

      const runs: ChecklistRun[] = [];
      querySnapshot.forEach((doc) => {
        runs.push({ id: doc.id, ...doc.data() } as ChecklistRun);
      });

      // Sort by startedAt descending (most recent first)
      runs.sort((a, b) => {
        const dateA = a.startedAt?.toDate ? a.startedAt.toDate().getTime() :
                     (a.startedAt instanceof Date ? a.startedAt.getTime() :
                     (typeof a.startedAt === 'number' ? a.startedAt : 0));
        const dateB = b.startedAt?.toDate ? b.startedAt.toDate().getTime() :
                     (b.startedAt instanceof Date ? b.startedAt.getTime() :
                     (typeof b.startedAt === 'number' ? b.startedAt : 0));
        return dateB - dateA;
      });

      return { success: true, data: runs };
    } catch (error: any) {
      console.error('Error getting active runs for user:', error);
      return { success: false, error: error.message };
    }
  },

  // Get active run for a specific checklist (optional context matching)
  getActiveRunForChecklist: async (
    checklistId: string,
    userId: string,
    context?: { locationId?: string; assetId?: string }
  ): Promise<{ success: boolean; data?: ChecklistRun; error?: string }> => {
    try {
      const runsRef = collection(db, 'checklist_runs');
      // First, get all active runs for this checklist and user
      const q = query(
        runsRef,
        where('checklistId', '==', checklistId),
        where('startedBy', '==', userId),
        where('status', 'in', ['in_progress', 'paused'])
      );
      const querySnapshot = await getDocs(q);

      const runs: ChecklistRun[] = [];
      querySnapshot.forEach((doc) => {
        runs.push({ id: doc.id, ...doc.data() } as ChecklistRun);
      });

      // If context provided, match by BOTH locationId AND assetId (if provided)
      // This ensures each location+asset combination has its own run
      if (context && runs.length > 0) {
        const matchedRun = runs.find(run => {
          // Must match locationId if provided in context
          if (context.locationId && run.locationId !== context.locationId) {
            return false;
          }
          
          // Must match assetId if provided in context
          if (context.assetId) {
            // Check direct assetId field
            if (run.assetId !== context.assetId) {
              // Also check variableValues for asset identifiers
              const runAssetId = run.variableValues?.['assetId'] || 
                                run.variableValues?.['asset'] ||
                                run.variableValues?.['Room Number'] || // Common variable name
                                run.variableValues?.['Room']; // Alternative
              
              if (runAssetId !== context.assetId) {
                return false;
              }
            }
          } else {
            // If context doesn't have assetId, run shouldn't have one either
            if (run.assetId) {
              return false;
            }
            // Also check if run has asset in variableValues
            if (run.variableValues?.['assetId'] || 
                run.variableValues?.['asset'] ||
                run.variableValues?.['Room Number'] ||
                run.variableValues?.['Room']) {
              return false;
            }
          }
          
          return true;
        });
        
        if (matchedRun) {
          return { success: true, data: matchedRun };
        }
      }

      // If no context or no match found, don't return any run
      // This ensures each location+asset gets its own run
      return { success: true, data: undefined };
    } catch (error: any) {
      console.error('Error getting active run for checklist:', error);
      return { success: false, error: error.message };
    }
  },

  // Pause a checklist run
  pauseRun: async (runId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const runRef = doc(db, 'checklist_runs', runId);
      const runDoc = await getDoc(runRef);
      
      if (!runDoc.exists()) {
        return { success: false, error: 'Checklist run not found' };
      }

      const run = runDoc.data() as ChecklistRun;
      
      if (run.status !== 'in_progress') {
        return { success: false, error: `Cannot pause run with status: ${run.status}` };
      }

      const now = new Date();
      const lastResumedAt = run.lastResumedAt?.toDate 
        ? run.lastResumedAt.toDate() 
        : (run.lastResumedAt instanceof Date 
            ? run.lastResumedAt 
            : new Date(run.startedAt?.toDate ? run.startedAt.toDate() : Date.now()));

      // Calculate active time since last resume
      const activeTimeSinceResume = Math.floor((now.getTime() - lastResumedAt.getTime()) / 1000);
      const newActiveTimeSeconds = run.activeTimeSeconds + activeTimeSinceResume;

      // Create pause duration entry
      // Note: Can't use serverTimestamp() in arrays, so we use a regular Date
      const pauseDurations = run.pauseDurations || [];
      const newPauseEntry: PauseDuration = {
        pausedAt: now, // Use regular Date object instead of serverTimestamp() for array items
      };
      pauseDurations.push(newPauseEntry);

      await updateDoc(runRef, {
        status: 'paused',
        pausedAt: serverTimestamp(),
        activeTimeSeconds: newActiveTimeSeconds,
        pauseDurations: pauseDurations,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error pausing checklist run:', error);
      return { success: false, error: error.message };
    }
  },

  // Resume a checklist run
  resumeRun: async (runId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const runRef = doc(db, 'checklist_runs', runId);
      const runDoc = await getDoc(runRef);
      
      if (!runDoc.exists()) {
        return { success: false, error: 'Checklist run not found' };
      }

      const run = runDoc.data() as ChecklistRun;
      
      if (run.status !== 'paused') {
        return { success: false, error: `Cannot resume run with status: ${run.status}` };
      }

      const now = new Date();
      const resumeTimestamp = serverTimestamp();
      const pauseDurations = run.pauseDurations || [];
      
      // Update the last pause entry with resume time
      // Note: Can't use serverTimestamp() in arrays, so we use a regular Date
      if (pauseDurations.length > 0) {
        const lastPause = pauseDurations[pauseDurations.length - 1];
        if (!lastPause.resumedAt) {
          lastPause.resumedAt = now; // Use regular Date for array item
          
          // Calculate pause duration if pausedAt is available
          if (lastPause.pausedAt) {
            const pausedAtDate = lastPause.pausedAt?.toDate 
              ? lastPause.pausedAt.toDate() 
              : (lastPause.pausedAt instanceof Date ? lastPause.pausedAt : new Date());
            lastPause.durationSeconds = Math.floor((now.getTime() - pausedAtDate.getTime()) / 1000);
          }
        }
      }

      await updateDoc(runRef, {
        status: 'in_progress',
        lastResumedAt: resumeTimestamp, // Use serverTimestamp() for top-level field
        pausedAt: null,
        pauseDurations: pauseDurations,
        updatedAt: resumeTimestamp,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error resuming checklist run:', error);
      return { success: false, error: error.message };
    }
  },

  // Update run progress (items, photos) without changing status
  updateRunProgress: async (
    runId: string,
    updates: {
      items?: Record<string, boolean>;
      photos?: Record<string, string>;
      itemTaskReferences?: Record<string, string[]>;
      variableValues?: Record<string, string>;
    }
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const runRef = doc(db, 'checklist_runs', runId);
      const updateData: Record<string, any> = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(runRef, updateData);
      return { success: true };
    } catch (error: any) {
      console.error('Error updating checklist run progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Complete a checklist run and create completion record
  completeRun: async (
    runId: string,
    completionData: {
      completedBy: string;
      completedByName?: string;
    }
  ): Promise<{ success: boolean; run?: ChecklistRun; error?: string }> => {
    try {
      const runRef = doc(db, 'checklist_runs', runId);
      const runDoc = await getDoc(runRef);
      
      if (!runDoc.exists()) {
        return { success: false, error: 'Checklist run not found' };
      }

      const run = runDoc.data() as ChecklistRun;
      const now = new Date();

      // If currently in progress, calculate final active time
      let finalActiveTimeSeconds = run.activeTimeSeconds;
      if (run.status === 'in_progress' && run.lastResumedAt) {
        const lastResumedAt = run.lastResumedAt?.toDate 
          ? run.lastResumedAt.toDate() 
          : (run.lastResumedAt instanceof Date ? run.lastResumedAt : now);
        const activeTimeSinceResume = Math.floor((now.getTime() - lastResumedAt.getTime()) / 1000);
        finalActiveTimeSeconds = run.activeTimeSeconds + activeTimeSinceResume;
      }

      // Calculate total time from start to completion
      const startedAtDate = run.startedAt?.toDate 
        ? run.startedAt.toDate() 
        : (run.startedAt instanceof Date ? run.startedAt : new Date());
      const totalTimeSeconds = Math.floor((now.getTime() - startedAtDate.getTime()) / 1000);

      // Update final pause durations (calculate duration for last pause if still paused)
      let finalPauseDurations = run.pauseDurations || [];
      if (run.status === 'paused' && finalPauseDurations.length > 0) {
        const lastPause = finalPauseDurations[finalPauseDurations.length - 1];
        if (lastPause.pausedAt && !lastPause.resumedAt) {
          // This pause never resumed, calculate duration to now
          const pausedAtDate = lastPause.pausedAt?.toDate 
            ? lastPause.pausedAt.toDate() 
            : (lastPause.pausedAt instanceof Date ? lastPause.pausedAt : now);
          lastPause.resumedAt = now;
          lastPause.durationSeconds = Math.floor((now.getTime() - pausedAtDate.getTime()) / 1000);
        }
      }

      // Update run to completed status
      await updateDoc(runRef, {
        status: 'completed',
        completedAt: serverTimestamp(),
        completedBy: completionData.completedBy,
        completedByName: completionData.completedByName,
        activeTimeSeconds: finalActiveTimeSeconds,
        pauseDurations: finalPauseDurations,
        updatedAt: serverTimestamp(),
      });

      // Get updated run
      const updatedRunDoc = await getDoc(runRef);
      const updatedRun = { id: updatedRunDoc.id, ...updatedRunDoc.data() } as ChecklistRun;

      return { 
        success: true, 
        run: {
          ...updatedRun,
          activeTimeSeconds: finalActiveTimeSeconds,
          // Add totalTimeSeconds for convenience (not stored in run, calculated)
        } as ChecklistRun & { totalTimeSeconds: number }
      };
    } catch (error: any) {
      console.error('Error completing checklist run:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a checklist run (for cleaning up abandoned runs)
  deleteRun: async (runId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const runRef = doc(db, 'checklist_runs', runId);
      await deleteDoc(runRef);
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting checklist run:', error);
      return { success: false, error: error.message };
    }
  },
};

